import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { FeedbackModal } from "./FeedbackModal";

type Variant = "sidebar" | "mobile-fab" | "floating";

/**
 * Trigger + modal pair for the Feedback button.
 * Self-contained: owns its own open/close state.
 */
export function FeedbackButton({ variant = "sidebar" }: { variant?: Variant }) {
  const [open, setOpen] = useState(false);

  // "mobile-fab" (legacy alias) and "floating" both render a fixed
  // bottom-right pill button visible on every breakpoint — the only way
  // unauthenticated visitors on public pages can reach the feedback form
  // (no sidebar / mobile nav is rendered for those surfaces).
  if (variant === "mobile-fab" || variant === "floating") {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          aria-label="Send feedback"
          title="Send feedback"
          className="fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full bg-stone-900 px-4 py-3 text-sm font-medium text-white shadow-lg transition hover:bg-stone-800 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2 lg:bottom-6"
          data-testid="feedback-fab"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="hidden sm:inline">Feedback</span>
        </button>
        <FeedbackModal isOpen={open} onClose={() => setOpen(false)} />
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-2xl px-4 py-2.5 text-sm font-medium text-stone-500 transition hover:bg-stone-100 hover:text-stone-900 active:scale-[0.98] active:bg-stone-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-1"
        data-testid="btn-feedback"
      >
        <span className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Send feedback
        </span>
        <span className="text-xs opacity-60">→</span>
      </button>
      <FeedbackModal isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}
